/**
 * In-page confirmation dialog with large touch targets.
 * `window.confirm` is avoided: it looks different in standalone PWAs and can
 * be blocked. The dialog is built from existing DOM (see HTML templates).
 */
export interface ConfirmOptions {
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
}

export function createConfirm(root: HTMLElement): (opts: ConfirmOptions) => Promise<boolean> {
  const messageEl = root.querySelector<HTMLElement>('[data-confirm-message]')!;
  const okBtn = root.querySelector<HTMLButtonElement>('[data-confirm-ok]')!;
  const cancelBtn = root.querySelector<HTMLButtonElement>('[data-confirm-cancel]')!;
  let pending: ((v: boolean) => void) | null = null;

  const finish = (value: boolean) => {
    root.hidden = true;
    const resolve = pending;
    pending = null;
    resolve?.(value);
  };
  okBtn.addEventListener('click', () => finish(true));
  cancelBtn.addEventListener('click', () => finish(false));
  root.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') finish(false);
  });

  return (opts) => {
    if (pending) finish(false);
    messageEl.textContent = opts.message;
    okBtn.textContent = opts.confirmLabel;
    cancelBtn.textContent = opts.cancelLabel ?? 'Abbrechen';
    root.hidden = false;
    // Focus the safe choice; a stray double tap lands on "cancel".
    cancelBtn.focus();
    return new Promise<boolean>((resolve) => {
      pending = resolve;
    });
  };
}
